import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  createPdfViewerUrl,
  getDocumentBackGroundColor,
  normalizeDocumentDataUrl,
} from "../../services/Functions";
import "./RcoActivityLogDialog.scss";

const isPreviewImageSrc = (src) => {
  if (!src) return false;
  const value = String(src);
  return (
    value.slice(0, 6) === "data:i" ||
    value.startsWith("blob:") ||
    /\.(png|jpe?g|gif|webp)(\?|$)/i.test(value)
  );
};

const RcoActivityLogPreviewContext = createContext(null);

export function useRcoActivityLogPreview() {
  return useContext(RcoActivityLogPreviewContext);
}

export function RcoActivityDocPreviewDialog({
  open,
  onClose,
  src,
  title = "Document Preview",
}) {
  const [resolvedSrc, setResolvedSrc] = useState(null);
  const [isPdfPreview, setIsPdfPreview] = useState(false);

  useEffect(() => {
    if (!open || !src) {
      setResolvedSrc(null);
      setIsPdfPreview(false);
      return undefined;
    }

    let blobUrl = null;
    const normalized = normalizeDocumentDataUrl(src);

    if (normalized && !isPreviewImageSrc(normalized)) {
      blobUrl = createPdfViewerUrl(src);
      setResolvedSrc(blobUrl || normalized);
      setIsPdfPreview(true);
    } else {
      setResolvedSrc(normalized || src);
      setIsPdfPreview(false);
    }

    return () => {
      if (blobUrl && blobUrl.startsWith("blob:")) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [open, src]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      className="rco-activity-doc-preview-dialog"
      sx={{ zIndex: 1500 }}
    >
      <DialogTitle className="rco-activity-doc-preview-dialog__title">
        <span>{title}</span>
        <button
          type="button"
          className="rco-activity-doc-preview-dialog__close"
          onClick={onClose}
          aria-label="Close preview"
        >
          <CloseIcon />
        </button>
      </DialogTitle>
      <DialogContent className="rco-activity-doc-preview-dialog__content">
        {resolvedSrc ? (
          isPdfPreview ? (
            <iframe
              src={resolvedSrc}
              title={title}
              className="rco-activity-doc-preview-dialog__iframe"
            />
          ) : (
            <img src={resolvedSrc} alt={title} />
          )
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function RcoHistoryField({ label, value, span = 1 }) {
  const spanClass =
    span === 2
      ? " rco-history-field--span2"
      : span === "full"
        ? " rco-history-field--full"
        : "";

  return (
    <div className={`wf-detail-item${spanClass}`}>
      <p className="prop-title">{label}</p>
      <p className="prop-detail">{value ?? "-"}</p>
    </div>
  );
}

export function RcoActivityLogClickableImage({
  src,
  label,
  children,
  className = "",
  style,
  onClick,
  disabled = false,
}) {
  const preview = useRcoActivityLogPreview();

  if (!src || disabled) {
    return children;
  }

  const handleClick = (event) => {
    if (onClick) {
      onClick(event);
      return;
    }
    preview?.openPreview?.(src, label || "Document Preview");
  };

  return (
    <button
      type="button"
      className={`rco-activity-log-clickable-image ${className}`.trim()}
      style={style}
      onClick={handleClick}
      aria-label={`Preview ${label || "document"}`}
    >
      {children}
    </button>
  );
}

export function RcoHistoryDoc({
  label,
  src,
  alt,
  onClick,
  enablePreview = true,
  documentFlag,
}) {
  const previewContext = useRcoActivityLogPreview();
  const [localPreviewOpen, setLocalPreviewOpen] = useState(false);

  if (!src) return null;

  const cardStyle =
    documentFlag !== undefined && documentFlag !== null && documentFlag !== ""
      ? { backgroundColor: getDocumentBackGroundColor(documentFlag) }
      : undefined;

  const handleClick = () => {
    if (onClick) {
      onClick(src);
      return;
    }
    if (!enablePreview) return;
    if (previewContext?.openPreview) {
      previewContext.openPreview(src, label || alt || "Document Preview");
      return;
    }
    setLocalPreviewOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className="wf-submitted-doc-card"
        style={cardStyle}
        onClick={handleClick}
        aria-label={`Preview ${label || alt || "document"}`}
      >
        <p className="wf-submitted-doc-card__label">{label}</p>
        <img src={src} alt={alt || label} />
      </button>
      {!previewContext ? (
        <RcoActivityDocPreviewDialog
          open={localPreviewOpen}
          onClose={() => setLocalPreviewOpen(false)}
          src={src}
          title={label || alt || "Document Preview"}
        />
      ) : null}
    </>
  );
}

export default function RcoActivityLogDialog({
  open,
  onClose,
  title,
  variant = "submitted",
  footer,
  headerAction,
  children,
  maxWidth = "md",
}) {
  const [previewState, setPreviewState] = useState({
    open: false,
    src: null,
    title: "Document Preview",
  });

  const previewContextValue = useMemo(
    () => ({
      openPreview: (src, title = "Document Preview") => {
        if (!src) return;
        setPreviewState({ open: true, src, title });
      },
      closePreview: () => {
        setPreviewState({ open: false, src: null, title: "Document Preview" });
      },
    }),
    []
  );

  const isAction = variant === "action";
  const rootClass = isAction
    ? "wf-action-taken-dialog rco-activity-log-dialog"
    : "wf-submitted-dialog rco-activity-log-dialog";
  const paperClass = isAction
    ? "wf-action-taken-dialog__paper"
    : "wf-submitted-dialog__paper";
  const contentClass = isAction
    ? "wf-action-taken-dialog__content"
    : "wf-submitted-dialog__content";
  const footerClass = isAction
    ? "wf-action-taken-dialog__footer"
    : "wf-submitted-dialog__footer";
  const footerTextClass = isAction
    ? "wf-action-taken-dialog__footer-text"
    : "wf-submitted-dialog__footer-text";

  return (
    <RcoActivityLogPreviewContext.Provider value={previewContextValue}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={maxWidth}
        className={rootClass}
        PaperProps={{ className: paperClass }}
      >
        <DialogTitle className="dialog-title">
          <div className="wf-dialog-header">
            <Typography className="wf-dialog-title-text">{title}</Typography>
            <div className="wf-dialog-header__actions">
              {headerAction}
              <button
                type="button"
                className="wf-dialog-close"
                onClick={onClose}
                aria-label="Close dialog"
              >
                <CloseIcon id="clearPopup" />
              </button>
            </div>
          </div>
        </DialogTitle>
        <DialogContent className={contentClass}>{children}</DialogContent>
        {footer ? (
          <DialogActions className={footerClass}>
            <p className={footerTextClass}>{footer}</p>
          </DialogActions>
        ) : null}
      </Dialog>
      <RcoActivityDocPreviewDialog
        open={previewState.open}
        onClose={previewContextValue.closePreview}
        src={previewState.src}
        title={previewState.title}
      />
    </RcoActivityLogPreviewContext.Provider>
  );
}
